/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react';
import { string, bool, object } from 'prop-types';
import { Link } from 'react-router-dom';
import classNames from 'clsx';
import Truncate from 'react-truncate';

import p from 'polyglot/polyglot';

import { isExternal } from 'utils/urlHelper';

import FormatDate from 'components/FormatDate/FormatDate';
import MapWPCategories from 'components/MapWPCategories/MapWPCategories';
import Button from 'components/Button/Button';

import styles from './article-card.scss';

/**
 * This is the ArticleCard component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
const ArticleCard = ({
  title,
  date,
  content,
  excerpt,
  categories,
  image,
  url,
  isCompact,
  hasSquareImage,
  hasButton,
}) => {
  const regex = /(<([^>]+)>)/gi;
  const excerptStripped = excerpt ? excerpt.replace(regex, '') : null;

  return (
    <article className={styles.wrapper}>
      <header
        className={classNames(styles.image, {
          [styles.square]: hasSquareImage,
        })}
        style={image ? { backgroundImage: `url(${image})` } : null}
      >
        <div
          className={classNames(styles.header, {
            [styles.wide]: isCompact,
          })}
        >
          {date && <FormatDate className={styles.date} date={date} />}

          <h2
            className={classNames(styles.title, {
              [styles.small]: isCompact,
            })}
            dangerouslySetInnerHTML={{ __html: title }}
          />

          {Object.keys(categories).length > 0 && (
            <ul className={styles.categories}>
              <MapWPCategories
                className={styles.category}
                categories={categories}
              />
            </ul>
          )}
        </div>
      </header>

      {content && <p className={styles.content}>{content}</p>}

      {excerpt && (
        <div className={styles.content}>
          <Truncate lines={3}>
            <span dangerouslySetInnerHTML={{ __html: excerptStripped }} />
          </Truncate>
        </div>
      )}

      {url && !isExternal(url) && <Link to={url} className={styles.link} />}

      {hasButton && url && (
        <Button className={styles.button} micro dark path={url}>
          {p.t('general.read_more')}
        </Button>
      )}
    </article>
  );
};

/**
 * Declare expected property types.
 *
 * @type {Object}
 */
ArticleCard.propTypes = {
  title: string.isRequired,
  date: string,
  image: string,
  content: string,
  excerpt: string,
  url: string,
  categories: object,
  isCompact: bool,
  hasSquareImage: bool,
  hasButton: bool,
};

ArticleCard.defaultProps = {
  date: null,
  image: null,
  content: null,
  excerpt: null,
  url: null,
  categories: {},
  isCompact: false,
  hasSquareImage: false,
  hasButton: false,
};

export default ArticleCard;
